import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowUpTrayIcon, TrashIcon, ArrowDownTrayIcon, ArrowTopRightOnSquareIcon,
  PlayCircleIcon, PhotoIcon, LinkIcon, FolderIcon, ChevronRightIcon, HomeIcon, FolderPlusIcon,
  CalendarDaysIcon, PencilSquareIcon, ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Tip } from '../components/ui/tip';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { confirmDialog } from '../components/ConfirmDialog';
import { promptDialog } from '../components/PromptDialog';

interface DriveStatus { connected: boolean; driveEnabled: boolean; folderConfigured?: boolean; folderName?: string | null; folderId?: string | null; folderUrl?: string | null; }
interface MediaFile { id: string; name: string; mimeType: string; kind: 'image' | 'video'; webViewLink?: string | null; createdTime?: string | null; size?: number | null; width?: number | null; height?: number | null; }
interface DriveFolder { id: string; name: string; }

const fmtSize = (b?: number | null) => {
  if (!b) return '';
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Miniatura: trae el thumbnail con auth (axios) y lo muestra como blob
const Thumb: React.FC<{ file: MediaFile }> = ({ file }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revoked = false; let obj: string | null = null;
    axios.get(`/api/google-drive/files/${file.id}/thumbnail`, { responseType: 'blob' })
      .then(r => { if (revoked) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setErr(true));
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [file.id]);
  if (err || !url) {
    return <div className="w-full h-full flex items-center justify-center bg-muted">{file.kind === 'video' ? <PlayCircleIcon className="h-10 w-10 text-muted-foreground" /> : <PhotoIcon className="h-10 w-10 text-muted-foreground" />}</div>;
  }
  return <img src={url} alt={file.name} className="w-full h-full object-cover" />;
};

const MM = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
function monthGrid(year: number, month: number) {
  const off = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const dim = new Date(year, month, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < off; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return cells;
}

const MiniCalendar: React.FC<{ sel: { y: number; m: number; d: number } | null; onPick: (y: number, m: number, d: number) => void }> = ({ sel, onPick }) => {
  const today = new Date();
  const [vy, setVy] = useState(sel?.y ?? today.getFullYear());
  const [vm, setVm] = useState(sel?.m ?? today.getMonth() + 1);
  const prev = () => { if (vm === 1) { setVm(12); setVy(vy - 1); } else setVm(vm - 1); };
  const next = () => { if (vm === 12) { setVm(1); setVy(vy + 1); } else setVm(vm + 1); };
  const isToday = (d: number) => today.getDate() === d && today.getMonth() + 1 === vm && today.getFullYear() === vy;
  const [marked, setMarked] = useState<Set<number>>(new Set());
  useEffect(() => {
    let dead = false;
    axios.get('/api/content-calendars/days-with-content', { params: { year: vy, month: vm } })
      .then(r => { if (!dead) setMarked(new Set(r.data.days || [])); })
      .catch(() => { if (!dead) setMarked(new Set()); });
    return () => { dead = true; };
  }, [vy, vm]);
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={prev}><ChevronLeftIcon className="h-4 w-4" /></Button>
        <span className="text-sm font-semibold">{MM[vm - 1]} {vy}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={next}><ChevronRightIcon className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-1">
        {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {monthGrid(vy, vm).map((d, i) => d === null ? <div key={i} /> : (
          <button key={i} onClick={() => onPick(vy, vm, d)}
            className={`relative h-8 rounded-md text-sm transition-colors ${sel && sel.y === vy && sel.m === vm && sel.d === d ? 'bg-black text-white dark:bg-white dark:text-black font-semibold' : isToday(d) ? 'ring-1 ring-blue-500/50 hover:bg-muted' : 'hover:bg-muted'}`}>
            {d}
            {marked.has(d) && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${sel && sel.y === vy && sel.m === vm && sel.d === d ? 'bg-white dark:bg-black' : 'bg-emerald-500'}`} />}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> días con material cargado</p>
    </div>
  );
};

const ContentMedia: React.FC = () => {
  const [status, setStatus] = useState<DriveStatus | null>(null);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [path, setPath] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [viewer, setViewer] = useState<MediaFile | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cargar el contenido completo del archivo abierto (con auth → blob)
  useEffect(() => {
    if (!viewer) { setViewerUrl(null); return; }
    let obj: string | null = null; let cancelled = false;
    setViewerUrl(null);
    axios.get(`/api/google-drive/files/${viewer.id}/content`, { responseType: 'blob' })
      .then(r => { if (cancelled) return; obj = URL.createObjectURL(r.data); setViewerUrl(obj); })
      .catch(() => { if (!cancelled) toast.error('No se pudo cargar el archivo'); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [viewer]);

  const fetchStatus = useCallback(async () => {
    try { const r = await axios.get('/api/google-drive/status'); setStatus(r.data); return r.data as DriveStatus; }
    catch { setStatus({ connected: false, driveEnabled: false }); return null; }
  }, []);

  const fetchFiles = useCallback(async (folderId?: string) => {
    setLoading(true);
    try {
      const r = await axios.get('/api/google-drive/files', { params: folderId ? { folderId } : {} });
      setFolders(r.data.folders || []); setFiles(r.data.files || []);
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudieron cargar los archivos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { (async () => {
    const s = await fetchStatus();
    if (s?.driveEnabled && s?.folderConfigured && s.folderId) {
      setPath([{ id: s.folderId, name: s.folderName || 'ROCAM' }]);
      fetchFiles(s.folderId);
    } else setLoading(false);
  })(); }, [fetchStatus, fetchFiles]);

  const currentFolderId = path.length ? path[path.length - 1].id : (status?.folderId || undefined);
  const openFolder = (f: DriveFolder) => { setPath(p => [...p, f]); fetchFiles(f.id); };
  const goToCrumb = (i: number) => { const np = path.slice(0, i + 1); setPath(np); fetchFiles(np[i].id); };

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const createFolder = async () => {
    if (!newFolderName.trim()) { toast.error('Poné un nombre'); return; }
    setCreatingFolder(true);
    try {
      await axios.post('/api/google-drive/folders', { name: newFolderName.trim(), parentId: currentFolderId });
      toast.success('Carpeta creada');
      setNewFolderOpen(false); setNewFolderName('');
      fetchFiles(currentFolderId);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al crear la carpeta'); }
    finally { setCreatingFolder(false); }
  };

  const connect = async () => {
    try { const r = await axios.get('/api/google-calendar/auth-url'); window.location.href = r.data.url; }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error al conectar'); }
  };

  const [folderLink, setFolderLink] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);
  const [reconfigFolder, setReconfigFolder] = useState(false);
  const saveFolder = async () => {
    if (!folderLink.trim()) { toast.error('Pegá el link de la carpeta'); return; }
    setSavingFolder(true);
    try {
      const r = await axios.post('/api/google-drive/folder', { link: folderLink.trim() });
      setStatus(r.data); setReconfigFolder(false); setFolderLink(''); toast.success(`Carpeta "${r.data.folder?.name || ''}" configurada`);
      setPath([{ id: r.data.folderId, name: r.data.folder?.name || 'ROCAM' }]);
      fetchFiles(r.data.folderId);
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo configurar la carpeta'); }
    finally { setSavingFolder(false); }
  };

  const doUpload = async (fileList: FileList | File[]) => {
    const arr = Array.from(fileList).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!arr.length) { toast.error('Subí imágenes o videos'); return; }
    const fd = new FormData();
    arr.forEach(f => fd.append('files', f));
    if (currentFolderId) fd.append('folderId', currentFolderId);
    setUploading(true);
    try {
      await axios.post('/api/google-drive/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${arr.length} archivo${arr.length > 1 ? 's' : ''} subido${arr.length > 1 ? 's' : ''}`);
      fetchFiles(currentFolderId);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al subir'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const remove = async (f: MediaFile) => {
    if (!(await confirmDialog({ title: 'Eliminar archivo', description: `¿Eliminar "${f.name}"? Va a la papelera de Drive.`, confirmText: 'Eliminar' }))) return;
    try { await axios.delete(`/api/google-drive/files/${f.id}`); toast.success('Eliminado'); setFiles(fs => fs.filter(x => x.id !== f.id)); setViewer(null); }
    catch { toast.error('Error al eliminar'); }
  };

  const download = async (f: MediaFile) => {
    try {
      const r = await axios.get(`/api/google-drive/files/${f.id}/content`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = f.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error('Error al descargar'); }
  };

  // Usar un item en una fecha (copia a la carpeta del día + marca el base como UTILIZADO)
  const [useFor, setUseFor] = useState<MediaFile | null>(null);
  const [useSel, setUseSel] = useState<{ y: number; m: number; d: number } | null>(null);
  const [usingDate, setUsingDate] = useState(false);
  const assignToDate = async () => {
    if (!useFor || !useSel) { toast.error('Elegí un día'); return; }
    setUsingDate(true);
    try {
      const r = await axios.post('/api/content-calendars/use-file', { fileId: useFor.id, year: useSel.y, month: useSel.m, day: useSel.d });
      toast.success(`Asignado a ${r.data.dateLabel || `${useSel.d}/${useSel.m}`}`);
      setUseFor(null); setUseSel(null); setViewer(null);
      fetchFiles(currentFolderId);
    } catch (e: any) { toast.error(e.response?.data?.error || 'No se pudo asignar'); }
    finally { setUsingDate(false); }
  };

  const renameFile = async (f: MediaFile) => {
    const current = f.name.replace(/^\[UTILIZADO[^\]]*\]\s*/, '');
    const name = await promptDialog({ title: 'Renombrar archivo', defaultValue: current, confirmText: 'Renombrar' });
    if (!name || name.trim() === current) return;
    try {
      await axios.post(`/api/google-drive/files/${f.id}/rename`, { name: name.trim() });
      toast.success('Renombrado'); setViewer(null); fetchFiles(currentFolderId);
    } catch (e: any) { toast.error(e.response?.data?.error || 'Error al renombrar'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Banco de medios</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Fotos y videos para el contenido, guardados en Google Drive</p>
        </div>
        {status?.driveEnabled && status?.folderConfigured && !reconfigFolder && (
          <div className="flex flex-wrap gap-2 items-center">
            {status.folderUrl && (
              <a href={status.folderUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5 max-w-[60vw]"><LinkIcon className="h-4 w-4 shrink-0" /> <span className="truncate">{status.folderName || 'Ver carpeta'}</span></Button>
              </a>
            )}
            <Button variant="ghost" size="sm" className="h-9 rounded-lg text-xs text-muted-foreground" onClick={() => setReconfigFolder(true)}>Cambiar carpeta</Button>
            <Button variant="outline" size="sm" className="h-9 rounded-lg gap-1.5" onClick={() => { setNewFolderName(''); setNewFolderOpen(true); }}>
              <FolderPlusIcon className="h-4 w-4" /> Nueva carpeta
            </Button>
            <Button size="sm" className="h-9 rounded-lg gap-1.5 bg-black dark:bg-white text-white dark:text-black" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <ArrowUpTrayIcon className="h-4 w-4" /> {uploading ? 'Subiendo...' : 'Subir archivos'}
            </Button>
            <input ref={inputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={e => e.target.files && doUpload(e.target.files)} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
      ) : !status?.connected ? (
        <Card><CardContent className="py-16 text-center space-y-3">
          <PhotoIcon className="h-12 w-12 text-muted-foreground mx-auto" />
          <p className="font-medium">Conectá la cuenta de Google primero</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Andá a <b>Redes → Eventos (Google)</b> y conectá la cuenta del taller. Después volvé acá.</p>
        </CardContent></Card>
      ) : !status?.driveEnabled ? (
        <Card><CardContent className="py-16 text-center space-y-4">
          <PhotoIcon className="h-12 w-12 text-muted-foreground mx-auto" />
          <div>
            <p className="font-medium">Activá el banco de medios</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">Hay que darle permiso a Google Drive. Reconectá la cuenta para activarlo (en la pantalla de Google, aceptá el permiso de Drive).</p>
          </div>
          <Button onClick={connect} className="bg-black dark:bg-white text-white dark:text-black gap-2"><LinkIcon className="h-4 w-4" /> Activar con Google</Button>
        </CardContent></Card>
      ) : (!status?.folderConfigured || reconfigFolder) ? (
        <Card><CardContent className="py-12 max-w-xl mx-auto space-y-5">
          <div className="text-center space-y-2">
            <PhotoIcon className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="font-medium">Elegí la carpeta de Drive</p>
            <p className="text-sm text-muted-foreground">Pegá el link de tu carpeta de Drive (la que compartiste con la cuenta del taller). Ahí se guardan las fotos y videos.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Link de la carpeta</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={folderLink} onChange={e => setFolderLink(e.target.value)} placeholder="https://drive.google.com/drive/folders/..." onKeyDown={e => { if (e.key === 'Enter') saveFolder(); }} autoFocus />
              <Button onClick={saveFolder} disabled={savingFolder} className="shrink-0">{savingFolder ? 'Guardando...' : 'Usar carpeta'}</Button>
              {reconfigFolder && status?.folderConfigured && <Button variant="outline" onClick={() => { setReconfigFolder(false); setFolderLink(''); }} className="shrink-0">Cancelar</Button>}
            </div>
            <p className="text-[11px] text-muted-foreground">La carpeta tiene que estar compartida como <b>Editor</b> con la cuenta conectada (rocamventas).</p>
          </div>
        </CardContent></Card>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files) doUpload(e.dataTransfer.files); }}
          className={`rounded-xl transition-colors ${dragOver ? 'ring-2 ring-blue-500/50 bg-blue-500/5' : ''}`}
        >
          {/* Breadcrumb */}
          {path.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap text-sm mb-3 overflow-x-auto">
              {path.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && <ChevronRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <button onClick={() => goToCrumb(i)} className={`flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted shrink-0 max-w-[55vw] ${i === path.length - 1 ? 'font-semibold' : 'text-muted-foreground'}`}>
                    {i === 0 && <HomeIcon className="h-4 w-4 shrink-0" />}<span className="truncate">{c.name}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {folders.length === 0 && files.length === 0 ? (
            <Card><CardContent className="py-16 text-center space-y-3">
              <ArrowUpTrayIcon className="h-12 w-12 text-muted-foreground mx-auto" />
              <p className="font-medium">Esta carpeta está vacía</p>
              <p className="text-sm text-muted-foreground">Subí fotos o videos con el botón, o arrastralos acá.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {/* Subcarpetas */}
              {folders.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {folders.map(fo => (
                    <button key={fo.id} onClick={() => openFolder(fo)} className="flex items-center gap-2 rounded-lg border border-border px-3 py-3 hover:bg-muted/50 transition-colors text-left">
                      <FolderIcon className="h-6 w-6 text-blue-500 shrink-0" />
                      <span className="text-sm font-medium truncate">{fo.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Archivos */}
              {files.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {files.map(f => (
                    <Card key={f.id} className="overflow-hidden group cursor-pointer" onClick={() => setViewer(f)}>
                      <div className="relative aspect-square bg-muted">
                        <Thumb file={f} />
                        {f.kind === 'video' && <div className="absolute inset-0 flex items-center justify-center"><PlayCircleIcon className="h-12 w-12 text-white/90 drop-shadow" /></div>}
                        <Tip label="Eliminar">
                          <button onClick={e => { e.stopPropagation(); remove(f); }} className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-red-600 text-white rounded-md p-1">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </Tip>
                      </div>
                      <div className="p-2">
                        <Tip label={f.name}><p className="text-xs font-medium truncate">{f.name}</p></Tip>
                        <p className="text-[11px] text-muted-foreground">{fmtSize(f.size)}{f.width ? ` · ${f.width}×${f.height}` : ''}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nueva carpeta */}
      {newFolderOpen && (
        <Dialog open onOpenChange={o => { if (!o) setNewFolderOpen(false); }}>
          <DialogContent className="w-[95vw] max-w-sm">
            <div className="space-y-4">
              <p className="font-semibold">Nueva carpeta</p>
              <p className="text-xs text-muted-foreground -mt-2">Se crea dentro de: {path.length ? path[path.length - 1].name : 'la carpeta raíz'}</p>
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Ej: Detailing, Octubre, Reels..." autoFocus onKeyDown={e => { if (e.key === 'Enter') createFolder(); }} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNewFolderOpen(false)}>Cancelar</Button>
                <Button onClick={createFolder} disabled={creatingFolder}>{creatingFolder ? 'Creando...' : 'Crear'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Visor */}
      {viewer && (
        <Dialog open onOpenChange={o => { if (!o) setViewer(null); }}>
          <DialogContent className="w-[95vw] max-w-3xl">
            <div className="space-y-3">
              <div className="bg-black/5 dark:bg-white/5 rounded-lg overflow-hidden flex items-center justify-center min-h-[200px] max-h-[70vh]">
                {!viewerUrl
                  ? <div className="py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
                  : viewer.kind === 'video'
                    ? <video src={viewerUrl} controls autoPlay className="max-w-[90vw] max-h-[70vh] w-full" />
                    : <img src={viewerUrl} alt={viewer.name} className="max-w-[90vw] max-h-[70vh] w-auto object-contain" />}
              </div>
              <p className="text-sm font-medium break-words">{viewer.name}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" className="gap-1.5 bg-black dark:bg-white text-white dark:text-black" onClick={() => { setUseFor(viewer); setUseSel(null); }}><CalendarDaysIcon className="h-4 w-4" /> Usar en fecha</Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => renameFile(viewer)}><PencilSquareIcon className="h-4 w-4" /> Renombrar</Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => download(viewer)}><ArrowDownTrayIcon className="h-4 w-4" /> Descargar</Button>
                {viewer.webViewLink && <a href={viewer.webViewLink} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="gap-1.5"><ArrowTopRightOnSquareIcon className="h-4 w-4" /> Drive</Button></a>}
                <Button variant="ghost" size="sm" className="gap-1.5 text-red-600 hover:bg-red-500/10 ml-auto" onClick={() => remove(viewer)}><TrashIcon className="h-4 w-4" /> Eliminar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Usar en fecha */}
      {useFor && (
        <Dialog open onOpenChange={o => { if (!o) setUseFor(null); }}>
          <DialogContent className="w-[95vw] max-w-sm">
            <div className="space-y-4">
              <div>
                <p className="font-semibold">Usar en una fecha</p>
                <p className="text-xs text-muted-foreground mt-0.5 break-words">{useFor.name.replace(/^\[UTILIZADO[^\]]*\]\s*/, '')}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Elegí el día del posteo</Label>
                <MiniCalendar sel={useSel} onPick={(y, m, d) => setUseSel({ y, m, d })} />
                {useSel && <p className="text-xs text-muted-foreground">Seleccionado: <b>{String(useSel.d).padStart(2, '0')}/{String(useSel.m).padStart(2, '0')}/{useSel.y}</b></p>}
                <p className="text-[11px] text-muted-foreground">Se copia a la carpeta de ese día y el original queda marcado como <b>UTILIZADO</b>.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setUseFor(null)}>Cancelar</Button>
                <Button onClick={assignToDate} disabled={usingDate || !useSel}>{usingDate ? 'Asignando...' : 'Asignar'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContentMedia;
